import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ReceiptText, AlertTriangle, AlertCircle, RefreshCw, Loader2,
  CheckCircle2, Clock, TrendingUp, TrendingDown, FileText,
  Sparkles, X, Edit3, FileDown, Pencil, ExternalLink, Send,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../../foundation/altzero_base/base/utils/supabaseClient';
import { billService } from '../../services/billService';
import { nemAnalysisService } from '../../services/nemAnalysisService';
import { ShadowBillDocument } from './ShadowBillDocument';
import { InvoicePdfViewer } from './InvoicePdfViewer';
import { BillComparisonTable } from './BillComparisonTable';
import { ExpectedInvoiceEditor } from './ExpectedInvoiceEditor';
import { isShadowOverridden, recalcShadowTotals } from '../../utils/shadowOverride';
import { splitLegacyQuantity } from '../../utils/comparisonRows';
import {
  applyExclusions, applyExclusionsToActual, effectiveExclusions, isSectionExcluded,
} from '../../utils/calcExclusions';
import { ParsedInvoiceEditor } from './ParsedInvoiceEditor';
import { BillReportScreen } from './BillReportScreen';
import { SendBillReportDialog } from './SendBillReportDialog';
import { BillRunChooser } from './BillRunChooser';
import { effectiveInvoiceResult } from '../../lib/effectiveInvoice';
import { networkIncomplete } from '../../lib/reportStatus';
import { useBillVerdicts } from '../../hooks/useBillVerdicts';
import { EMPTY, billToForm, UsageFormFields } from './SiteBillsTab';
import type { BvBill, BvBillFormData, ActualBillResult, ShadowBillResult, CalcExclusion } from '../../types/billTypes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Storage paths are `{orgId}/bills/{billId}/{uuid}_{original name}` — show the
// human filename, not the whole key.
function invoiceFileName(path: string) {
  const base = path.split('/').pop() ?? path;
  return base.replace(/^[0-9a-fA-F-]{36}_/, '');
}


type MainTab = 'comparison' | 'shadow' | 'notes' | 'report';

// ─── Component ────────────────────────────────────────────────────────────────

type Props = {
  billId: string;
  onBillIdChange: (id: string) => void;
  /** Used for the chooser when no bill is selected yet (site-level). */
  siteId?: string;
  nmi?: string | null;
  /** Jumps to this site's contract configuration. Shadow figures come straight
   *  from the contracts, so checking one against the other used to mean leaving
   *  the bill run and hunting for the contracts section by hand. */
  onOpenContracts?: () => void;
  /** Jumps to the site's Invoices section. The invoice PDF, parsing and the
   *  parsed-line editor all live there now rather than as a Bill Run tab —
   *  a bill run is about comparing figures, not managing documents. */
  onOpenInvoices?: () => void;
};

export function BillRunDetail({ billId, onBillIdChange, siteId, nmi: propNmi, onOpenContracts, onOpenInvoices }: Props) {
  const [bill, setBill] = useState<BvBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [editingExpected, setEditingExpected] = useState(false);
  const [recalcError, setRecalcError] = useState<string | null>(null);
  // Job 53: charges switched out of the expected invoice while validating. Mirrors
  // bv_bill.calc_exclusions; kept in local state so the toggles feel immediate.
  const [exclusions, setExclusions] = useState<CalcExclusion[]>([]);

  // Result of "Use NEM Analysis". Named autoPop* until job 70 removed the
  // Auto-Populate button these were shared with.
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);
  const [nemApplying, setNemApplying] = useState(false);

  const [mainTab, setMainTab] = useState<MainTab>('comparison');
  // Job 19: invoice PDF collapsed under the comparison so a disputed line can be
  // checked against the document without leaving the tab.
  const [showComparisonInvoice, setShowComparisonInvoice] = useState(false);

  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [hasSiteAttachments, setHasSiteAttachments] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [sendReportOpen, setSendReportOpen] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [reportStatus, setReportStatus] = useState<string>('');
  const [reportNoteType, setReportNoteType] = useState<string>('');
  const [reportNoteManual, setReportNoteManual] = useState<string>('');
  const [savingReportSettings, setSavingReportSettings] = useState(false);
  const [reportSettingsSaved, setReportSettingsSaved] = useState(false);


  // ── Load bill ──────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    if (!billId) { setBill(null); setLoading(false); return; }
    let mounted = true;
    setLoading(true);
    billService.getById(billId).then((res) => {
      if (!mounted) return;
      if (res.success && res.data) { setBill(res.data); setError(null); }
      else setError(res.error ?? 'Failed to load bill');
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [billId]);

  useEffect(() => { return load(); }, [load]);

  useEffect(() => {
    if (!bill?.site_id || bill.invoice_path) { setHasSiteAttachments(false); return; }
    let mounted = true;
    supabase
      .from('bv_attachment')
      .select('id', { count: 'exact', head: true })
      .eq('site_id', bill.site_id)
      .in('category', ['invoice', 'electricity_contract'])
      .then(({ count }) => { if (mounted) setHasSiteAttachments((count ?? 0) > 0); });
    return () => { mounted = false; };
  }, [bill?.site_id, bill?.invoice_path]);

  useEffect(() => {
    const rd = bill?.report_data;
    setReportStatus(rd?.validation_status ?? '');
    setReportNoteType(rd?.customer_note_type ?? '');
    setReportNoteManual(rd?.customer_note_manual ?? '');
  }, [bill?.report_data]);


  // Job 53: keep the local exclusion toggles in step with what's persisted.
  useEffect(() => {
    setExclusions(Array.isArray(bill?.calc_exclusions) ? bill!.calc_exclusions! : []);
  }, [bill?.id, bill?.calc_exclusions]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleRecalculate = async () => {
    if (!bill) return;
    // Job 52: recalculating discards a hand-corrected expected invoice. That may
    // well be what the user wants after fixing the underlying contract, but it
    // must not happen without them knowing.
    if (isShadowOverridden(bill.shadow_result) && !confirm(
      'This expected invoice was edited manually'
      + (bill.shadow_result?.override_note ? ` (${bill.shadow_result.override_note})` : '')
      + '.\n\nRecalculating replaces those edits with freshly calculated values. Continue?',
    )) return;

    setRecalculating(true);
    setRecalcError(null);
    try {
      const saved = await billService.recalculate(bill);
      if (saved.success && saved.data) setBill(saved.data);
      else setRecalcError(saved.error ?? 'Failed to save result');
    } catch (err) {
      setRecalcError(err instanceof Error ? err.message : 'Calculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  // Job 53: persist a changed exclusion list. Optimistic — the toggle flips at
  // once and the write follows; a failed write reverts and surfaces the error.
  const persistExclusions = async (next: CalcExclusion[]) => {
    if (!bill) return;
    const prev = exclusions;
    setExclusions(next);
    setRecalcError(null);
    const saved = await billService.setCalcExclusions(bill.id, next);
    if (saved.success && saved.data) setBill(saved.data);
    else {
      // Revert, and say why. A switch that silently springs back is
      // indistinguishable from a broken switch.
      setExclusions(prev);
      const reason = saved.error ?? 'unknown error';
      // PostgREST reports a missing column as PGRST204 / "schema cache", which
      // means migration 093_bv_bill_calc_exclusions.sql has not run on this
      // database — worth naming, because no amount of retrying will fix it.
      const missingColumn = /calc_exclusions/i.test(reason) && /column|schema cache/i.test(reason);
      setRecalcError(
        missingColumn
          ? `Could not switch that charge off: this database has no bv_bill.calc_exclusions column. `
            + `Migration 093_bv_bill_calc_exclusions.sql needs to be applied. (${reason})`
          : `Could not switch that charge off: ${reason}`,
      );
    }
  };

  // The per-charge and per-section toggling now lives in BillComparisonTable,
  // which holds the paired rows those decisions are made against. This component
  // only persists whatever list the table hands back.

  // Populate the bill from a completed Site NEM Analysis run (GCS data
  // pipeline). Since job 70 this is the ONLY way to populate usage here — the
  // hardcoded-schedule "Auto-Populate kWh" it replaced is gone (see the note
  // where its button used to be).
  const handleUseNemAnalysis = async () => {
    if (!bill) return;
    setNemApplying(true);
    setApplyError(null);
    setApplySuccess(null);
    try {
      const runRes = await nemAnalysisService.getRunForPeriod(
        bill.site_id, bill.org_id, bill.bill_from, bill.bill_to,
      );
      const run = runRes.success ? runRes.data : null;
      if (!run || run.tou_status !== 'done') {
        throw new Error(
          'No completed NEM Analysis for this bill period — run Steps 1-3 on the NEM Analysis tab first.',
        );
      }
      const applied = await nemAnalysisService.applyToBill(run.id, bill.id);
      if (!applied.success || !applied.data) throw new Error(applied.error ?? 'Apply failed');

      const refreshed = await billService.getById(bill.id);
      const warningsNote = applied.data.warnings.length ? ` — ${applied.data.warnings.join('; ')}` : '';
      // Same rule as auto-populate: don't discard a manual override (job 52).
      if (isShadowOverridden(refreshed.data?.shadow_result)) {
        if (refreshed.success && refreshed.data) setBill(refreshed.data);
        setApplySuccess(
          `Applied from NEM Analysis (${applied.data.retail.total_kwh.toLocaleString()} kWh). `
          + `The expected invoice was edited manually, so it was left as-is — use Refresh to replace it.${warningsNote}`,
        );
        return;
      }
      if (refreshed.success && refreshed.data) {
        const saved = await billService.recalculate(refreshed.data);
        if (!saved.success || !saved.data) throw new Error(saved.error ?? 'Failed to recalculate');
        setBill(saved.data);
      }
      setApplySuccess(
        `Applied from NEM Analysis (${applied.data.retail.total_kwh.toLocaleString()} kWh) & expected invoice recalculated${warningsNote}`,
      );
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Failed to apply NEM Analysis');
    } finally {
      setNemApplying(false);
    }
  };

  // Job 52: save a hand-corrected expected invoice.
  const handleSaveExpectedOverride = async (result: ShadowBillResult, note: string) => {
    if (!bill) return;
    const saved = await billService.saveShadowOverride(bill.id, result, note);
    if (!saved.success || !saved.data) throw new Error(saved.error ?? 'Failed to save override');
    setBill(saved.data);
    setEditingExpected(false);
  };

  // A line stored before QTY and Usage were separated holds one figure in
  // `volume` whatever it measures, and the table reads it through the same
  // classifier. Writing an edit onto the raw line would leave the old `volume`
  // in place beside the new `usage`, and the stale figure would reappear under
  // QTY. Normalising first means what is saved is what was on screen.
  const normalise = <T extends { volume?: number | null; volume_unit?: string | null; usage?: number | null; usage_unit?: string | null; description: string; amount: number }>(l: T): T => {
    const s = splitLegacyQuantity(l);
    return {
      ...l,
      volume: s.volume ?? undefined,
      volume_unit: s.volumeUnit ?? undefined,
      usage: s.usage,
      usage_unit: s.usageUnit,
    };
  };

  // Job 20: "usage not adjustable, needs to be". Both quantities were already
  // editable — expected in ExpectedInvoiceEditor, retailer in ParsedInvoiceEditor
  // — but each on its own tab, while the wrong quantity is found here, reading
  // the two sides against each other. These write the same way those editors do,
  // so there is one save path per side and not two.
  //
  // Only the quantity moves. The amount is deliberately left alone: rate units
  // differ line to line (¢/kWh, $/day, $/meter/year), so re-deriving an amount
  // from volume × rate would produce confidently wrong money — the reasoning
  // already recorded in utils/shadowOverride.ts.
  const editShadowLine = async (
    sourceIndex: number, patch: Partial<ShadowBillResult['lines'][number]>,
  ) => {
    if (!bill || !sr) return;
    const lines = sr.lines.map((l, i) => (i === sourceIndex ? { ...normalise(l), ...patch } : l));
    // Totals are recomputed from amounts, which have not changed — this keeps
    // section totals consistent rather than assuming they are untouched.
    await handleSaveExpectedOverride(
      recalcShadowTotals(lines, sr),
      'Quantity corrected from the comparison',
    );
  };

  const editActualLine = async (
    sourceIndex: number,
    patch: Partial<ActualBillResult['lines'][number]>,
    note = 'Quantity corrected from the comparison',
  ) => {
    if (!bill) return;
    // The EFFECTIVE invoice, so a second correction builds on the first rather
    // than on the untouched parse.
    const current = effectiveInvoiceResult(bill) as ActualBillResult | null;
    if (!current) return;
    const lines = current.lines.map((l, i) => (i === sourceIndex ? { ...normalise(l), ...patch } : l));
    // Through saveActualResult, never a direct write: the server route re-derives
    // bv_bill.peak_kwh and re-prices, so the expected side cannot be left costing
    // a quantity the retailer side no longer claims.
    const res = await billService.saveActualResult(
      bill.id, { ...current, lines }, note,
    );
    if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to save the correction');
    setBill(res.data);
  };

  // QTY and Usage are corrected independently — metering is a meter count over a
  // number of days, and either can be the wrong one.
  const handleEditShadowQty = (i: number, volume: number | null) =>
    editShadowLine(i, { volume: volume ?? undefined });
  const handleEditShadowUsage = (i: number, usage: number | null) =>
    editShadowLine(i, { usage });
  const handleEditActualQty = (i: number, volume: number | null) =>
    editActualLine(i, { volume });
  const handleEditActualUsage = (i: number, usage: number | null) =>
    editActualLine(i, { usage });
  // Renaming ONE invoice line, on this bill only. The retailer-wide charge alias renames every
  // line that reads the same and carries into future invoices, which is what made a single
  // mapping look like it "maps anything with a similar name also".
  const handleEditActualDescription = (i: number, description: string) =>
    editActualLine(i, { description }, `Invoice line renamed to "${description}" from the comparison`);

  const handleGenerateReport = async () => {
    if (!bill) return;
    setGeneratingReport(true);
    setReportError(null);
    try {
      const reportData = { generated_at: new Date().toISOString() };
      const res = await billService.saveReportData(bill.id, reportData);
      if (res.success && res.data) setBill(res.data);
      setMainTab('report');
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleSaveReportSettings = async () => {
    if (!bill) return;
    setSavingReportSettings(true);
    try {
      const newData = {
        ...(bill.report_data ?? {}),
        generated_at: bill.report_data?.generated_at ?? new Date().toISOString(),
        ...(reportStatus ? { validation_status: reportStatus as import('../../types/billTypes').BvReportData['validation_status'] } : { validation_status: undefined }),
        ...(reportNoteType ? { customer_note_type: reportNoteType as import('../../types/billTypes').BvReportData['customer_note_type'] } : { customer_note_type: undefined }),
        ...(reportNoteManual.trim() ? { customer_note_manual: reportNoteManual.trim() } : { customer_note_manual: undefined }),
      };
      const res = await billService.saveReportData(bill.id, newData);
      if (res.success && res.data) { setBill(res.data); setReportSettingsSaved(true); setTimeout(() => setReportSettingsSaved(false), 2500); }
    } catch { /* ignore */ } finally {
      setSavingReportSettings(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current || !bill) return;
    setGeneratingReport(true);
    setReportError(null);
    try {
      const { downloadReportAsPdf } = await import('../../utils/pdfUtils');
      const target = (reportRef.current.querySelector('[data-pdf-target]') ?? reportRef.current) as HTMLElement;
      await downloadReportAsPdf(
        target,
        `BillValidation_${bill.site?.nmi ?? bill.id}.pdf`,
      );
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setGeneratingReport(false);
    }
  };

  // The verdict on the exclusions as toggled on screen, not as last saved.
  const verdictBills = useMemo(() => (bill ? [{ ...bill, calc_exclusions: exclusions }] : []), [bill, exclusions]);
  const verdictFor = useBillVerdicts(verdictBills);

  // ── Empty state: no bill selected yet (site level) ─────────────────────────
  if (!billId) {
    return (
      <BillRunChooser
        siteId={siteId ?? ''}
        currentBillId=""
        onSelect={onBillIdChange}
      />
    );
  }

  if (loading) {
    return (
      <div data-testid="bill-detail-loading" className="space-y-4">
        <div className="h-28 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-20 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-96 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div data-testid="bill-detail-error" className="flex flex-col items-center justify-center py-24 text-muted-foreground">
        <ReceiptText className="h-14 w-14 mb-4 opacity-20" />
        <p className="text-base font-medium mb-1">{error ?? 'Bill not found'}</p>
      </div>
    );
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const sr = bill.shadow_result;
  // Job 53: the report reads both sides with the excluded charges taken out. The
  // exclusion has to leave BOTH — the Comparison table drops it from each side
  // for exactly this reason, and dropping it from the expected side alone would
  // report the excluded charge to the client as the whole variance.
  // `exclusions` is what the user toggled and what gets written back. The totals
  // additionally honour the site-level GreenPower rule — a section dropped from
  // the comparison has to leave the totals, or the report prints rows that do
  // not add up to the total beneath them.
  const effective = effectiveExclusions(bill, exclusions);
  const gstExcluded = isSectionExcluded('gst', effective);
  const rawAr = effectiveInvoiceResult(bill) as ActualBillResult | null;
  const reportAr = rawAr ? applyExclusionsToActual(rawAr, effective) : null;
  const nmi = bill.site?.nmi ?? propNmi ?? 'Unknown NMI';
  const orgName = bill.site?.client?.name ?? '';
  const warnings = sr?.warnings ?? [];
  // Whole bill on both sides, differing only by what we validate (billVerdict).
  const verdict = verdictFor(verdictBills[0] ?? bill);
  const expectedTotal = verdict?.expectedTotal ?? sr?.total ?? null;
  const actualTotal = verdict?.invoiceTotal ?? bill.actual_total ?? null;
  const variance = verdict?.variance ?? null;
  // Network charges could not be priced (usage data does not cover the period,
  // so the bill run never ran): the "variance" is the charges we did not price,
  // not a retailer error. Shown as INCOMPLETE rather than an over/undercharge.
  const incomplete = networkIncomplete(bill);
  const varianceStatus = !verdict ? null
    : incomplete ? 'INCOMPLETE'
    : verdict.bucket === 'overcharge' ? 'OVERCHARGE'
    : verdict.bucket === 'undercharge' ? 'UNDERCHARGE'
    : 'MATCHED';

  const MAIN_TABS: { id: MainTab; label: string }[] = [
    { id: 'comparison', label: 'Comparison' },
    { id: 'shadow', label: 'Expected Invoice' },
    { id: 'notes', label: 'Notes' },
    { id: 'report', label: 'Report' },
  ];

  return (
    <div data-testid="bill-detail" data-bill-id={billId} className="space-y-5">

      {/* ── Header Card ───────────────────────────────────────────────────── */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div
                className="p-3 rounded-xl flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}
              >
                <ReceiptText className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Job 41: NMI links to the site; a react-router Link honours
                      Cmd/Ctrl-click and middle-click for a new tab. Not nested in
                      any other row/click handler. */}
                  <h2 className="text-xl font-bold font-mono text-gray-900">
                    {bill.site_id ? (
                      <Link
                        to={`/bill-validation/sites/${bill.site_id}`}
                        className="hover:text-blue-700 hover:underline"
                        title="Open site — Cmd/Ctrl-click for a new tab"
                      >
                        {nmi}
                      </Link>
                    ) : (
                      nmi
                    )}
                  </h2>
                  {/* Job 41: open the site's (or its client's) Shield drive folder.
                      Only rendered when a link exists — no dead icon otherwise. */}
                  {(() => {
                    const drive = bill.site?.drive_link ?? bill.site?.client?.drive_link;
                    return drive ? (
                      <a
                        href={drive}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-700"
                        title="Open drive folder"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null;
                  })()}
                  {!sr ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                      <Clock className="h-3 w-3" /> Pending
                    </span>
                  ) : warnings.length > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle2 className="h-3 w-3" /> Calculated
                    </span>
                  )}
                  {varianceStatus && (
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold',
                      varianceStatus === 'INCOMPLETE' ? 'bg-slate-100 text-slate-700' :
                      varianceStatus === 'OVERCHARGE' ? 'bg-red-100 text-red-700' :
                      varianceStatus === 'UNDERCHARGE' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700',
                    )}
                    data-testid="bill-variance-status"
                    title={varianceStatus === 'INCOMPLETE'
                      ? 'Network charges are not priced yet, so the expected total is missing them. The bill run needs usage data for the whole period.'
                      : undefined}
                    >
                      {varianceStatus === 'INCOMPLETE' && <AlertTriangle className="h-3 w-3" />}
                      {varianceStatus === 'OVERCHARGE' && <TrendingUp className="h-3 w-3" />}
                      {varianceStatus === 'UNDERCHARGE' && <TrendingDown className="h-3 w-3" />}
                      {varianceStatus === 'MATCHED' && <CheckCircle2 className="h-3 w-3" />}
                      {varianceStatus}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mt-1">
                  {orgName && <span className="font-medium text-gray-700">{orgName}</span>}
                  {orgName && <span className="text-gray-300">·</span>}
                  <span>{fmtDate(bill.bill_from)} – {fmtDate(bill.bill_to)}</span>
                  <span className="text-gray-300">·</span>
                  <span>{bill.billing_days ?? '?'} days</span>
                  <span className="text-gray-300">·</span>
                  <span>Created {new Date(bill.created_at).toLocaleDateString('en-AU')}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => void handleUseNemAnalysis()}
                disabled={nemApplying || recalculating}
                title="Populate kWh from the site's completed NEM Analysis run (GCS data) and recalculate the expected invoice"
              >
                {nemApplying
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Sparkles className="h-4 w-4" />}
                {nemApplying ? 'Applying…' : 'Use NEM Analysis'}
              </Button>
              {/* Job 70 removed "Auto-Populate kWh" from here. It was the
                  legacy path and it wrote figures that were wrong on almost
                  every site:
                    * TOU windows came from `bv_extn.state ?? 'QLD'`, and
                      bv_extn.state was set on 3 of 400 sites — the real state
                      is site.addr_state (VIC 148 / NSW 135 / QLD 55 / SA 50 in
                      dev), so most sites were banded as Queensland.
                    * the retail schedule key came from
                      `bv_extn.retail_tou_schedule`, set on 0 of 400 sites, so
                      RETAIL_TOU_SCHEDULES['flat'] was used every time and the
                      peak/shoulder/off-peak split it wrote meant nothing.
                    * with no intervals for the period it wrote zeros
                      unconditionally — the same "turns the usage to zero"
                      this job reported, which had only been fixed on
                      applyToBill's side.
                  Use NEM Analysis replaces it: same purpose, bands from
                  retailer_tou_rates, and it refuses rather than zeroing.
                  POST /bills/:id/auto-populate-usage still exists and is now
                  unreferenced by the UI. */}
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={handleRecalculate}
                disabled={recalculating || nemApplying}
              >
                {recalculating
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RefreshCw className="h-4 w-4" />}
                {recalculating ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Bill Run Chooser ──────────────────────────────────────────────── */}
      <BillRunChooser
        siteId={bill.site_id}
        currentBillId={bill.id}
        onSelect={onBillIdChange}
      />

      {/* ── KPI Strip ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Usage</p>
            <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
              {bill.total_kwh != null ? `${bill.total_kwh.toLocaleString()}` : '—'}
              {bill.total_kwh != null && <span className="text-sm font-normal text-gray-500 ml-1">kWh</span>}
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Total</p>
            <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
              {expectedTotal != null ? fmtCurrency(expectedTotal) : <span className="text-gray-300">—</span>}
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Actual Invoice</p>
            <p className={cn('text-xl font-bold mt-1 tabular-nums', actualTotal != null ? 'text-gray-900' : 'text-gray-300')}>
              {actualTotal != null ? fmtCurrency(actualTotal) : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className={cn(
          'border-0 shadow-sm',
          varianceStatus === 'INCOMPLETE' ? 'bg-slate-50' :
          varianceStatus === 'OVERCHARGE' ? 'bg-red-50' :
          varianceStatus === 'UNDERCHARGE' ? 'bg-amber-50' :
          varianceStatus === 'MATCHED' ? 'bg-green-50' : '',
        )}>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Variance</p>
            {variance != null ? (
              <p className={cn(
                'text-xl font-bold mt-1 tabular-nums',
                varianceStatus === 'INCOMPLETE' ? 'text-slate-500' :
                varianceStatus === 'OVERCHARGE' ? 'text-red-700' :
                varianceStatus === 'UNDERCHARGE' ? 'text-amber-700' :
                'text-green-700',
              )}>
                {variance >= 0 ? '+' : ''}{fmtCurrency(variance)}
              </p>
            ) : (
              <p className="text-xl font-bold text-gray-300 mt-1">—</p>
            )}
            {variance != null && incomplete && (
              <p data-testid="bill-variance-incomplete" className="text-[11px] text-slate-600 mt-0.5">
                Network charges not priced yet — not a real variance
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Warnings ──────────────────────────────────────────────────────── */}
      {warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />
            Calculation Warnings
          </h4>
          <ul className="space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                <span className="mt-0.5">•</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {applySuccess && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {applySuccess}
        </div>
      )}
      {applyError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {applyError}
        </div>
      )}

      {/* ── Main Tabs ─────────────────────────────────────────────────────── */}
      <div>
        <div className="flex border-b border-gray-200 gap-0 mb-5 overflow-x-auto">
          {MAIN_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`bill-tab-${t.id}`}
              data-active={mainTab === t.id}
              onClick={() => setMainTab(t.id)}
              className={cn(
                'px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                mainTab === t.id
                  ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Errors are shown above the tabs, not inside one. The exclusion toggles
            moved to the Comparison tab (Job 53) while this banner was still inside
            the Expected Invoice branch, so a failed save reverted the switch and
            said nothing — the switch looked broken. */}
        {recalcError && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{recalcError}</span>
          </div>
        )}

        {/* ── Expected Invoice Tab ────────────────────────────────────────────── */}
        {mainTab === 'shadow' && (
          <div className="space-y-3">
            {!sr ? (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-200 rounded-xl text-muted-foreground">
                <ReceiptText className="h-12 w-12 mb-4 opacity-20" />
                <p className="text-base font-semibold mb-1">Expected invoice not yet calculated</p>
                <p className="text-sm mb-4 text-center max-w-sm">
                  Click Refresh to generate an expected invoice using the contracts configured for this site.
                </p>
                <Button
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  className="gap-2"
                >
                  {recalculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {recalculating ? 'Calculating…' : 'Calculate Expected Invoice'}
                </Button>
              </div>
            ) : editingExpected ? (
              <ExpectedInvoiceEditor
                initialResult={sr}
                onSave={handleSaveExpectedOverride}
                onCancel={() => setEditingExpected(false)}
              />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs">
                  <span className="text-muted-foreground">
                    {isShadowOverridden(sr)
                      ? 'Some figures below were edited manually and are no longer purely calculated.'
                      : "Every figure below is derived from this site's contracts."}
                  </span>
                  <div className="flex items-center gap-2">
                    {onOpenContracts && (
                      <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={onOpenContracts}>
                        <FileText className="h-3.5 w-3.5" /> View contracts
                      </Button>
                    )}
                    {onOpenInvoices && (
                      <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={onOpenInvoices}>
                        <ReceiptText className="h-3.5 w-3.5" /> Open Invoices
                      </Button>
                    )}
                    {/* Job 52 */}
                    <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={() => setEditingExpected(true)}>
                      <Pencil className="h-3.5 w-3.5" /> Edit values
                    </Button>
                  </div>
                </div>

                {isShadowOverridden(sr) && (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">Manually adjusted</span>
                      {sr.overridden_at && ` on ${new Date(sr.overridden_at).toLocaleString('en-AU')}`}
                      {sr.override_note ? ` — ${sr.override_note}` : ''}.
                      {' '}Recalculating will replace these values, and asks first.
                    </span>
                  </div>
                )}

                {/* Job 53: exclusions are shown here (struck through, reflected in
                    the totals) but switched on the Comparison tab, which is where
                    a validator is actually working when they decide to park a
                    charge. Two places to do it was one place too many to find. */}
                <ShadowBillDocument
                  bill={bill}
                  result={sr}
                  exclusions={effective}
                />
              </>
            )}
          </div>
        )}

        {/* ── Comparison Tab ────────────────────────────────────────────── */}
        {mainTab === 'comparison' && (
          <div className="space-y-4">
            {!sr ? (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-200 rounded-xl text-muted-foreground">
                <ReceiptText className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm font-medium">Expected invoice not yet calculated</p>
                <p className="text-xs mt-1">Click Refresh to generate the expected invoice first</p>
              </div>
            ) : (
              <BillComparisonTable
                bill={bill}
                shadowResult={sr}
                actualResult={effectiveInvoiceResult(bill) as ActualBillResult | null ?? null}
                exclusions={exclusions}
                onExclusionsChange={(next) => void persistExclusions(next)}
                onEditShadowQty={handleEditShadowQty}
                onEditActualQty={handleEditActualQty}
                onEditActualDescription={handleEditActualDescription}
                onEditShadowUsage={handleEditShadowUsage}
                onEditActualUsage={handleEditActualUsage}
                // Rows whose quantity sums several lines cannot take a single
                // typed figure, so they hand off to the editor that can.
                onOpenExpectedEditor={() => { setMainTab('shadow'); setEditingExpected(true); }}
                onOpenRetailerEditor={onOpenInvoices}
              />
            )}

            {/* Job 19: the invoice PDF, collapsed by default, below the comparison
                so a disputed line can be checked against the document in place. */}
            {bill.invoice_path ? (
              <Card className="border shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowComparisonInvoice((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0">
                    <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="truncate">Invoice — {invoiceFileName(bill.invoice_path)}</span>
                  </div>
                  {showComparisonInvoice
                    ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                </button>
                {showComparisonInvoice && (
                  <CardContent className="pt-0 pb-4">
                    <InvoicePdfViewer
                      bill={bill}
                      onInvoiceUpdated={(updated) => setBill(updated)}
                    />
                  </CardContent>
                )}
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground px-1">
                No invoice is attached to this bill.
              </p>
            )}
          </div>
        )}

        {/* ── Notes Tab ─────────────────────────────────────────────────── */}
        {mainTab === 'notes' && (
          <div className="space-y-4">

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700">Internal Notes</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {bill.notes ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{bill.notes}</p>
                ) : (
                  <p className="text-sm italic text-muted-foreground">No internal notes for this bill.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-semibold text-gray-700">Report Customer Message</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Controls what the client sees in the PDF validation report</p>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">

                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">Validation Status Override</label>
                  <select
                    value={reportStatus}
                    onChange={e => setReportStatus(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Auto (based on accuracy %)</option>
                    <option value="validated_approved">✓ Validated and Approved</option>
                    <option value="discrepancy_detected">! Discrepancy Detected</option>
                    <option value="missing_invoice">⚠ Missing Invoice</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto mode: 100% → matched message · 95–99% → acceptable variance · &lt;95% → below acceptable
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">Pre-Written Customer Note</label>
                  <select
                    value={reportNoteType}
                    onChange={e => setReportNoteType(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    <option value="overcharge">Overcharge — investigating with retailer</option>
                    <option value="undercharge">Undercharge — potential undercharge notice</option>
                    <option value="gst">GST — GST variance explanation</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">Additional Custom Note</label>
                  <textarea
                    value={reportNoteManual}
                    onChange={e => setReportNoteManual(e.target.value)}
                    rows={3}
                    placeholder="Optional: add a custom message to show in the report…"
                    className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    onClick={() => void handleSaveReportSettings()}
                    disabled={savingReportSettings}
                    className="gap-1.5"
                  >
                    {savingReportSettings ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                    {savingReportSettings ? 'Saving…' : 'Save Report Settings'}
                  </Button>
                  {reportSettingsSaved && (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Saved
                    </span>
                  )}
                </div>

              </CardContent>
            </Card>

          </div>
        )}

        {/* ── Report Tab ─────────────────────────────────────────────────── */}
        {mainTab === 'report' && (
          <div className="space-y-4">
            {(!sr || !bill.actual_parsed_result) ? (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-200 rounded-xl text-muted-foreground">
                <ReceiptText className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm font-medium">Report not ready</p>
                <p className="text-xs mt-1 max-w-sm text-center">
                  Generate an expected invoice (Usage tab → Save) and parse an invoice (Invoice tab) first.
                </p>
              </div>
            ) : !bill.report_data ? (
              <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-200 rounded-xl text-muted-foreground">
                <ReceiptText className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm font-medium mb-1">Report not generated yet</p>
                <Button size="sm" onClick={() => void handleGenerateReport()} disabled={generatingReport} className="gap-1.5 mt-3">
                  {generatingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                  {generatingReport ? 'Generating…' : 'Generate Report'}
                </Button>
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' }}>
                      <ReceiptText className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-base font-bold text-gray-900 leading-tight">Bill Validation Report</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {nmi} · {fmtDate(bill.bill_from)} – {fmtDate(bill.bill_to)}
                        {bill.site?.client?.name && <span className="ml-1">· {bill.site.client.name}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(() => {
                      const kind = verdict?.bucket === 'overcharge' ? 'over'
                        : verdict?.bucket === 'undercharge' ? 'under' : 'matched';
                      return (
                        <span className={cn(
                          'hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold',
                          kind === 'matched' ? 'bg-green-100 text-green-700' :
                          kind === 'over' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700',
                        )}>
                          {kind === 'matched' ? <CheckCircle2 className="h-3 w-3" /> :
                           kind === 'over' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {kind === 'matched' ? 'Matched' : kind === 'over' ? 'Overcharged' : 'Undercharged'}
                        </span>
                      );
                    })()}
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => void handleDownloadPdf()} disabled={generatingReport}>
                      {generatingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                      {generatingReport ? 'Downloading…' : 'Download PDF'}
                    </Button>
                    <Button size="sm" className="gap-2" onClick={() => setSendReportOpen(true)}>
                      <Send className="h-4 w-4" />
                      Send Report
                    </Button>
                  </div>
                </div>

                {reportError && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <X className="h-3.5 w-3.5 flex-shrink-0" />
                    {reportError}
                  </div>
                )}

                <div className="flex justify-center py-4">
                  <div ref={reportRef} className="w-full max-w-4xl shadow-2xl rounded-2xl overflow-hidden">
                    <BillReportScreen
                      bill={bill}
                      actualResult={reportAr as ActualBillResult}
                      shadowResult={applyExclusions(sr, effective)}
                      gstExcluded={gstExcluded}
                      exclusions={exclusions}
                      onEditNote={() => setMainTab('notes')}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>

      {sr && bill.actual_parsed_result && (
        <SendBillReportDialog
          open={sendReportOpen}
          onClose={() => setSendReportOpen(false)}
          bill={bill}
          shadowResult={applyExclusions(sr, effective)}
          actualResult={reportAr as ActualBillResult}
          gstExcluded={gstExcluded}
        />
      )}
    </div>
  );
}